// Foco's legal page (/legal) as it stood when the version archive began (21 October 2026), stamped on the
// accounts that signed up from 13 October: the first archived version. (Until 15 October the page also carried
// "pending legal review" notes and named the company "AIKIT REVENUE, S.L.U."; the clauses were the same.)
// Archived: never edit it (tests/legal.test.ts pins its text). A change to the legal text is a new dated
// version (shared/legal.ts).
import { bilingual } from "../i18n";
import type { LegalText } from "../legal";

export default bilingual<LegalText>({
  en: {
    title: "Legal",
    sections: {
      notice: {
        title: "Legal notice",
        parts: [
          {
            p: "Under article 10 of Spain's Law 34/2002 on information society services (LSSI-CE), Foco belongs to:",
            list: [
              "Owner: AIKIT REVENUE, S.L.",
              "Tax ID (NIF): B27545912",
              "Address: Sector Literatos 38, 28760 Tres Cantos (Madrid, Spain)",
              "Registered in the Madrid Mercantile Registry, sheet M-883086, entry 1",
              "Contact: foco@superintelect.io",
            ],
          },
          {
            p: "TikTok, Claude and the names of other products are trademarks of their owners. Foco is not affiliated with or endorsed by them.",
          },
        ],
      },
      privacy: {
        title: "Privacy",
        parts: [
          {
            h: "Controller",
            p: "AIKIT REVENUE, S.L., with the contact details above.",
          },
          {
            h: "What we process and why",
            list: [
              "Your account: your email and a hash of your password, to sign you in and send you account emails (confirmation, password resets, credits). Legal basis: the contract.",
              "Your projects: your briefs and messages, the links and files you add, and the media and videos made for you, to make your videos. Legal basis: the contract.",
              "Credits and payments: your balance, what each video spent and, if you buy a plan or credits, your billing details, to charge you, invoice and keep accounts. Legal basis: the contract and our tax and accounting obligations. Card details go straight to our payment provider and never reach us.",
              "Security: the addresses that sign in or create accounts, to stop abuse. Legal basis: our legitimate interest in keeping the service safe.",
              "Support: the emails you send us and our replies, to help you. Legal basis: the contract and our legitimate interest in answering you.",
              "Usage statistics: as on any visit, our server's log records your IP address and browser, and the sign-in page notes what you see and press there, with no identifier or cookie. In the studio we note when your account reaches each step of making a video (first video, preview, render, download, purchase). We use this to see what works. Legal basis: our legitimate interest in improving Foco.",
            ],
          },
          {
            h: "Providers",
            p: "Providers process data on our behalf: Stripe (payments and invoices), Resend (sending and receiving email), OpenRouter and fal.ai (they pass your briefs and files to the AI models that make your videos, such as those of Anthropic and Google), Google (its Gemini API voices the narration), Google Cloud (backups) and Hetzner Online GmbH (servers for extra capacity). Your projects run on servers in the European Union. Some providers may process data outside the European Economic Area under the safeguards of the GDPR (an adequacy decision or standard contractual clauses).",
          },
          {
            h: "How long we keep it",
            p: "Your account and projects, while your account exists. Sign-ups whose email is never confirmed, 7 days. Billing records, for the periods tax and commercial law require. Support emails and our replies, 2 years from when we receive them.",
          },
          {
            h: "Your rights",
            p: "You can access, correct, delete, object to, restrict and port your data by writing to foco@superintelect.io. If you think we have not handled your request properly, you can complain to the Spanish Data Protection Agency (aepd.es).",
          },
        ],
      },
      terms: {
        title: "Terms of use",
        parts: [
          {
            h: "The service",
            p: "Foco makes short videos from a product link, files or a brief: the script, voice-over, images, music, captions and cuts. You review the result, ask for changes and download the video.",
          },
          {
            h: "Your account",
            p: "One account per person, with an email you control. Keep your password to yourself; you are responsible for what is done from your account. We may suspend an account used against these terms.",
          },
          {
            h: "Credits",
            list: [
              "Making videos spends credits; the studio shows what each video spent.",
              "A plan's credits renew each month and do not carry over. Extra credits last 12 months while you have a plan.",
              "Credits from a Señal purchase last 3 months from when you add them. If that purchase is refunded, the credits left end.",
              "Credits have no cash value and cannot be transferred.",
            ],
          },
          {
            h: "Your content",
            p: "What you add and the videos made for you are yours. You give us the permission we need to store and process them to make your videos. You must have the rights to what you add (logos, product photos, footage, people's images and voices) and you are responsible for how you publish your videos, including any label the law or a platform requires for AI-generated or altered content.",
          },
          {
            h: "Acceptable use",
            p: "Do not use Foco for unlawful content, to deceive people, to imitate a real person without their consent, or to infringe others' rights.",
          },
          {
            h: "AI-generated results",
            p: "Videos are made with AI models and may contain mistakes. Review each video before you publish it. We do not guarantee views, followers or sales.",
          },
          {
            h: "For professional use",
            p: "Foco is for businesses and people who use it in their work: selling, promoting products or making content for brands. When you pay, you confirm you are buying for that activity, so consumer protection rules, such as the right of withdrawal, do not apply.",
          },
          {
            h: "Payments",
            p: "Prices exclude VAT, which is added when you pay. Plans renew monthly until you cancel them.",
          },
          {
            h: "Liability",
            p: "Except for wilful misconduct or gross negligence, our liability is limited to what you paid us in the previous 12 months and does not cover lost profits.",
          },
          {
            h: "Changes and law",
            p: "We may change the service and these terms. If we change your plan's price or these terms, we will tell you by email before the change applies. These terms are governed by Spanish law.",
          },
        ],
      },
      cookies: {
        title: "Cookies",
        parts: [
          {
            p: "Foco uses one technical cookie, cutline_session, which keeps you signed in for 7 days. While you add Señal credits, the link waits in this browser tab until it is used. Both are needed for the service, so they do not require consent. We use no analytics or advertising cookies.",
          },
        ],
      },
    },
  },
  es: {
    title: "Información legal",
    sections: {
      notice: {
        title: "Aviso legal",
        parts: [
          {
            p: "Conforme al artículo 10 de la Ley 34/2002, de servicios de la sociedad de la información y de comercio electrónico (LSSI-CE), Foco es titularidad de:",
            list: [
              "Titular: AIKIT REVENUE, S.L.",
              "NIF: B27545912",
              "Domicilio: Sector Literatos 38, 28760 Tres Cantos (Madrid, España)",
              "Inscrita en el Registro Mercantil de Madrid, hoja M-883086, inscripción 1.ª",
              "Contacto: foco@superintelect.io",
            ],
          },
          {
            p: "TikTok, Claude y los nombres de otros productos son marcas de sus respectivos titulares. Foco no está afiliado a ellos ni cuenta con su respaldo.",
          },
        ],
      },
      privacy: {
        title: "Privacidad",
        parts: [
          {
            h: "Responsable del tratamiento",
            p: "AIKIT REVENUE, S.L., con los datos de contacto indicados más arriba.",
          },
          {
            h: "Qué datos tratamos y con qué finalidad",
            list: [
              "Tu cuenta: tu correo electrónico y un hash de tu contraseña, para que puedas iniciar sesión y para enviarte correos sobre tu cuenta (confirmación, restablecimiento de contraseña, créditos). Base jurídica: la ejecución del contrato.",
              "Tus proyectos: tus indicaciones y mensajes, los enlaces y archivos que añades, y los contenidos multimedia y vídeos creados para ti, para hacer tus vídeos. Base jurídica: la ejecución del contrato.",
              "Créditos y pagos: tu saldo, lo que ha gastado cada vídeo y, si compras un plan o créditos, tus datos de facturación, para cobrarte, facturar y llevar la contabilidad. Base jurídica: la ejecución del contrato y el cumplimiento de nuestras obligaciones fiscales y contables. Los datos de tu tarjeta van directamente a nuestro proveedor de pagos y nunca llegan a nosotros.",
              "Seguridad: las direcciones desde las que se inicia sesión o se crean cuentas, para impedir abusos. Base jurídica: nuestro interés legítimo en mantener la seguridad del servicio.",
              "Soporte: los correos que nos envías y nuestras respuestas, para ayudarte. Base jurídica: la ejecución del contrato y nuestro interés legítimo en responderte.",
              "Estadísticas de uso: como en cualquier visita, el registro del servidor anota tu dirección IP y tu navegador, y la página de acceso anota qué ves y pulsas en ella, sin identificadores ni cookies. En el estudio anotamos cuándo llega tu cuenta a cada paso de hacer un vídeo (primer vídeo, vista previa, render, descarga, compra). Lo usamos para ver qué funciona. Base jurídica: nuestro interés legítimo en mejorar Foco.",
            ],
          },
          {
            h: "Encargados del tratamiento",
            p: "Estos proveedores tratan datos por cuenta nuestra: Stripe (pagos y facturas), Resend (envío y recepción de correo), OpenRouter y fal.ai (hacen llegar tus indicaciones y archivos a los modelos de IA que crean tus vídeos, como los de Anthropic y Google), Google (su API Gemini pone voz a la locución), Google Cloud (copias de seguridad) y Hetzner Online GmbH (servidores para capacidad adicional). Tus proyectos se ejecutan en servidores de la Unión Europea. Algunos proveedores pueden tratar datos fuera del Espacio Económico Europeo con las garantías que establece el RGPD (una decisión de adecuación o cláusulas contractuales tipo).",
          },
          {
            h: "Plazos de conservación",
            p: "Tu cuenta y tus proyectos, mientras exista tu cuenta. Las altas cuyo correo no llega a confirmarse, 7 días. Los datos de facturación, durante los plazos que exige la normativa fiscal y mercantil. Los correos de soporte y nuestras respuestas, 2 años desde que los recibimos.",
          },
          {
            h: "Tus derechos",
            p: "Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad de tus datos escribiendo a foco@superintelect.io. Si consideras que no hemos atendido correctamente tu solicitud, puedes presentar una reclamación ante la Agencia Española de Protección de Datos (aepd.es).",
          },
        ],
      },
      terms: {
        title: "Condiciones de uso",
        parts: [
          {
            h: "El servicio",
            p: "Foco crea vídeos cortos a partir del enlace de un producto, de archivos o de unas indicaciones: el guion, la locución, las imágenes, la música, los subtítulos y el montaje. Tú revisas el resultado, pides cambios y descargas el vídeo.",
          },
          {
            h: "Tu cuenta",
            p: "Una cuenta por persona, con un correo electrónico que controles. No compartas tu contraseña; eres responsable de lo que se haga desde tu cuenta. Podemos suspender una cuenta que se use en contra de estas condiciones.",
          },
          {
            h: "Créditos",
            list: [
              "Crear vídeos consume créditos; el estudio muestra lo que ha gastado cada vídeo.",
              "Los créditos de un plan se renuevan cada mes y no se acumulan. Los créditos extra duran 12 meses mientras tengas un plan.",
              "Los créditos de una compra en Señal duran 3 meses desde que los añades. Si se reembolsa esa compra, los créditos que queden se extinguen.",
              "Los créditos no tienen valor monetario y no son transferibles.",
            ],
          },
          {
            h: "Tu contenido",
            p: "Lo que añades y los vídeos creados para ti son tuyos. Nos concedes la autorización necesaria para almacenarlos y tratarlos con el fin de hacer tus vídeos. Debes tener los derechos sobre lo que añades (logotipos, fotos de producto, grabaciones, la imagen y la voz de las personas) y eres responsable de cómo publicas tus vídeos, incluida cualquier etiqueta que la ley o una plataforma exijan para el contenido generado o alterado con IA.",
          },
          {
            h: "Uso aceptable",
            p: "No uses Foco para contenido ilícito, para engañar a otras personas, para imitar a una persona real sin su consentimiento ni para vulnerar derechos de terceros.",
          },
          {
            h: "Resultados generados con IA",
            p: "Los vídeos se hacen con modelos de IA y pueden contener errores. Revisa cada vídeo antes de publicarlo. No garantizamos visualizaciones, seguidores ni ventas.",
          },
          {
            h: "Para uso profesional",
            p: "Foco es para empresas y personas que lo usan en su trabajo: vender, promocionar productos o crear contenido para marcas. Al pagar, confirmas que compras para esa actividad; por eso no se aplican las normas de protección de los consumidores, como el derecho de desistimiento.",
          },
          {
            h: "Pagos",
            p: "Los precios no incluyen el IVA, que se suma al pagar. Los planes se renuevan cada mes hasta que los canceles.",
          },
          {
            h: "Responsabilidad",
            p: "Salvo dolo o culpa grave, nuestra responsabilidad se limita a lo que nos hayas pagado en los 12 meses anteriores y no cubre el lucro cesante.",
          },
          {
            h: "Cambios y legislación aplicable",
            p: "Podemos modificar el servicio y estas condiciones. Si cambiamos el precio de tu plan o estas condiciones, te avisaremos por correo antes de que se apliquen. Estas condiciones se rigen por la legislación española.",
          },
        ],
      },
      cookies: {
        title: "Cookies",
        parts: [
          {
            p: "Foco usa una única cookie técnica, cutline_session, que mantiene tu sesión iniciada durante 7 días. Mientras añades créditos de Señal, el enlace se guarda en esta pestaña del navegador hasta que se usa. Ambos son necesarios para prestar el servicio, por lo que no requieren consentimiento. No usamos cookies analíticas ni publicitarias.",
          },
        ],
      },
    },
  },
});
